/**
 * Compliance Health Card (MA-COMPLIANCE-VISIBILITY-002)
 * 
 * War Room KPI showing TrustedForm capture health metrics.
 * Displays % of leads missing TrustedForm URL with breakdowns.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck, AlertTriangle, Info, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';

// =============================================================================
// COMPLIANCE HEALTH CARD
// =============================================================================

export function ComplianceHealthCard({ period = 'mtd', campaignId, tenantId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['compliance-health', period, campaignId, tenantId],
    queryFn: async () => {
      const params = { period };
      if (campaignId && campaignId !== 'all') params.campaign_id = campaignId;
      if (tenantId) params.tenant_id = tenantId;
      const response = await api.get('/analytics/compliance-health', { params });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card className="glass border-white/10" data-testid="compliance-health-card">
        <CardContent className="p-5">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-700 rounded w-1/3"></div>
            <div className="h-8 bg-slate-700 rounded w-1/2"></div>
            <div className="h-2 bg-slate-700 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-slate-400">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Unable to load compliance health</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { metrics, by_funnel, by_mode } = data;
  const { total_leads, leads_with_cert, leads_missing_cert, missing_cert_percent } = metrics;
  
  // Determine health status
  const isHealthy = missing_cert_percent <= 5;
  const isDegraded = missing_cert_percent > 5 && missing_cert_percent <= 15;
  const isFailing = missing_cert_percent > 15;
  
  const healthColor = isHealthy ? 'emerald' : isDegraded ? 'amber' : 'red';
  const healthIcon = isHealthy ? ShieldCheck : isDegraded ? ShieldAlert : AlertTriangle;
  const HealthIcon = healthIcon;

  return (
    <Card className="glass border-white/10" data-testid="compliance-health-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg bg-${healthColor}-500/10`}>
              <HealthIcon className={`h-4 w-4 text-${healthColor}-400`} />
            </div>
            <div>
              <CardTitle className="text-sm font-medium text-slate-200">
                TrustedForm Capture Health
              </CardTitle>
              <CardDescription className="text-xs text-slate-400">
                Funnel leads with TrustedForm enabled
              </CardDescription>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="text-xs">
                  Measures what % of funnel leads are missing TrustedForm certificates.
                  Only includes leads from funnels with TrustedForm mode set to "capture_only" or "enforce".
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Primary Metric */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-bold ${
              isHealthy ? 'text-emerald-400' : isDegraded ? 'text-amber-400' : 'text-red-400'
            }`}>
              {missing_cert_percent.toFixed(1)}%
            </span>
            <span className="text-sm text-slate-400">Missing TrustedForm URL</span>
          </div>
          
          {/* Progress bar */}
          <div className="mt-2">
            <Progress 
              value={100 - missing_cert_percent} 
              className={`h-2 ${
                isHealthy ? 'bg-emerald-500/20' : isDegraded ? 'bg-amber-500/20' : 'bg-red-500/20'
              }`}
            />
          </div>
          
          {/* Supporting numbers */}
          <div className="flex justify-between mt-2 text-xs text-slate-400">
            <span>{leads_with_cert.toLocaleString()} with cert</span>
            <span>{leads_missing_cert.toLocaleString()} missing</span>
            <span>{total_leads.toLocaleString()} total</span>
          </div>
        </div>
        
        {/* Mode Breakdown */}
        {by_mode && Object.keys(by_mode).length > 0 && (
          <div className="border-t border-white/10 pt-3">
            <p className="text-xs text-slate-400 mb-2">By Mode</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(by_mode).map(([mode, stats]) => (
                <Badge 
                  key={mode}
                  variant="outline"
                  className={`text-xs ${
                    mode === 'enforce' 
                      ? 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30' 
                      : 'bg-blue-500/10 text-blue-300 border-blue-500/30'
                  }`}
                >
                  {mode === 'enforce' ? 'Enforce' : mode === 'capture_only' ? 'Capture Only' : mode}
                  : {stats.total} ({stats.quarantined || 0} quarantined)
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Top Funnels with Issues */}
        {by_funnel && by_funnel.length > 0 && by_funnel.some(f => f.missing_percent > 0) && (
          <div className="border-t border-white/10 pt-3 mt-3">
            <p className="text-xs text-slate-400 mb-2">Funnels with Missing Certs</p>
            <div className="space-y-1">
              {by_funnel
                .filter(f => f.missing_percent > 0)
                .slice(0, 3)
                .map((funnel, idx) => (
                  <div 
                    key={funnel.funnel_id || idx}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-slate-300 truncate max-w-[60%]">
                      {funnel.funnel_name}
                    </span>
                    <span className={`font-medium ${
                      funnel.missing_percent > 20 ? 'text-red-400' : 
                      funnel.missing_percent > 10 ? 'text-amber-400' : 
                      'text-slate-400'
                    }`}>
                      {funnel.missing_percent.toFixed(0)}% missing
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* Empty state */}
        {total_leads === 0 && (
          <div className="text-center py-4 text-slate-400 text-sm">
            No funnel leads with TrustedForm enabled in this period
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ComplianceHealthCard;
